// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Help center  (route /help — the "Help Center" row on the profile page)
// One page for "I need help": a search box that filters everything below it, the REAL contact channels (WhatsApp, phone, email — the
// same ones as the Contact page, with the hours it publishes), quick solutions that link to the screen that does the job, and an FAQ
// accordion. Content lives in data/helpCenter.ts. Deliberately NOT here (no data/system behind it): "24x7 concierge", "support online"
// status, response-time badges ("~1 min", "< 15 mins"), a toll-free number, a cold-chain fleet temperature, eco-box returns, lab /
// chemical certificates, voice search, "pier-to-plate" guarantee copy.
// Neighbours: pages/Contact (form + registered office), pages/Complaint, pages/RefundAssistance, data/legalContact, data/whatsappContext.
// ─────────────────────────────────────────────────────────────────────────────
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Clock, Mail, MessageCircle, Phone, Search } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { HELP_FAQS, QUICK_SOLUTIONS } from "../data/helpCenter";
import { useContactInfo } from "../hooks/useContactInfo";
import { getWhatsAppLink } from "../data/whatsappContext";
import useDocumentTitle from "../hooks/useDocumentTitle";

const card = "rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

export default function HelpCenter() {
  const info = useContactInfo();
  useDocumentTitle("Help center");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const baseId = useId();
  const term = query.trim().toLowerCase();
  const matches = (...parts: string[]) => term === "" || parts.some((part) => part.toLowerCase().includes(term));

  const solutions = QUICK_SOLUTIONS.filter((s) => matches(s.title, s.description));
  const faqs = HELP_FAQS.map((faq, index) => ({ faq, index })).filter(({ faq }) => (term === "" ? faq.help === true : true) && matches(faq.q, faq.a));
  const channels = [
    { id: "whatsapp", icon: MessageCircle, title: "WhatsApp", detail: "Chat with us", href: getWhatsAppLink("Hi ZenFresh, I need help with an order."), external: true, tone: "bg-whatsapp text-ocean-950" },
    { id: "call", icon: Phone, title: info.phoneDisplay, detail: "Call us", href: `tel:${info.phoneHref}`, external: false, tone: "bg-ocean-800 text-white" },
    { id: "email", icon: Mail, title: info.email, detail: "Email us", href: `mailto:${info.email}`, external: false, tone: "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50" },
  ].filter((c) => matches(c.title, c.detail, "contact whatsapp call phone email"));
  const nothing = solutions.length === 0 && faqs.length === 0 && channels.length === 0;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-5 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Help center</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Answers, or a person to ask</p>
            </div>
          </header>

          <Link to="/coming-soon/concierge-help" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Live concierge &amp; self-service help</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <section className="rounded-2xl bg-gradient-to-br from-ocean-900 via-ocean-800 to-ocean-900 p-4 text-foam-50 shadow-md">
            <h2 className="font-display text-lg font-bold leading-snug">How can we help you today?</h2>
            <p className="mt-1 text-sm text-foam-100/85">Search our answers, or reach us directly below.</p>
            <div className="mt-3 flex items-center rounded-xl bg-white px-3">
              <Search className="h-5 w-5 shrink-0 text-ocean-800" aria-hidden="true" />
              <label htmlFor={`${baseId}-search`} className="sr-only">Search help</label>
              <input
                id={`${baseId}-search`}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search cuts, refunds, wallet, delivery…"
                className="min-h-12 w-full bg-transparent px-2 text-sm text-ocean-950 outline-none placeholder:text-ocean-800/60"
              />
            </div>
          </section>
          <p role="status" className="sr-only">{term === "" ? "" : nothing ? "No results." : `${solutions.length + faqs.length + channels.length} results.`}</p>

          {nothing && (
            <div className={`${card} p-6 text-center`}>
              <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Nothing matches &ldquo;{query.trim()}&rdquo;</p>
              <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">Try another word, or write to us.</p>
              <Link to="/contact" className="mt-3 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 font-bold text-white hover:bg-ocean-700">Contact us</Link>
            </div>
          )}

          {channels.length > 0 && (
            <section aria-labelledby={`${baseId}-channels`} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <h2 id={`${baseId}-channels`} className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Talk to us</h2>
                <span className="flex items-center gap-1 text-xs text-ocean-800/80 dark:text-foam-100/75"><Clock className="h-3.5 w-3.5" aria-hidden="true" /> {info.hours}</span>
              </div>
              <ul className="flex flex-col gap-2">
                {channels.map(({ id, icon: Icon, title, detail, href, external, tone }) => (
                  <li key={id}>
                    <a
                      href={href}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className={`${card} flex min-h-16 items-center gap-3 p-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
                    >
                      <span aria-hidden="true" className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon className="h-6 w-6" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block break-all text-sm font-bold text-ocean-950 dark:text-foam-50">{title}</span>
                        <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{detail}{external && <span className="sr-only"> (opens in a new tab)</span>}</span>
                      </span>
                      <ChevronRight className="h-5 w-5 shrink-0 text-ocean-800/60 dark:text-foam-100/60" aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">
                Prefer a form? <Link to="/contact" className="font-bold underline">Write to us</Link> — you&apos;ll also find our registered office there.
              </p>
            </section>
          )}

          {solutions.length > 0 && (
            <section aria-labelledby={`${baseId}-solutions`} className="flex flex-col gap-2">
              <h2 id={`${baseId}-solutions`} className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Quick solutions</h2>
              <ul className="flex flex-col gap-2">
                {solutions.map((solution) => (
                  <li key={solution.id}>
                    <Link to={solution.to} className={`${card} flex min-h-16 items-center gap-3 p-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">{solution.title}</span>
                        <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{solution.description}</span>
                      </span>
                      <ChevronRight className="h-5 w-5 shrink-0 text-ocean-800/60 dark:text-foam-100/60" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {faqs.length > 0 && (
            <section aria-labelledby={`${baseId}-faqs`} className="flex flex-col gap-2">
              <h2 id={`${baseId}-faqs`} className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Frequently asked questions</h2>
              <ul className="flex flex-col gap-2">
                {faqs.map(({ faq, index }) => {
                  const expanded = open === index || term !== "";
                  return (
                    <li key={faq.q} className={card}>
                      <h3>
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-controls={`${baseId}-faq-${index}`}
                          onClick={() => setOpen(open === index ? null : index)}
                          className="flex min-h-14 w-full items-center justify-between gap-3 rounded-2xl p-4 text-left text-sm font-bold text-ocean-950 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-foam-50"
                        >
                          {faq.q}
                          <ChevronDown className={`h-5 w-5 shrink-0 text-ocean-800 transition-transform motion-reduce:transition-none dark:text-foam-100 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
                        </button>
                      </h3>
                      <div id={`${baseId}-faq-${index}`} hidden={!expanded} className="px-4 pb-4 text-sm text-ocean-800/90 dark:text-foam-100/85">
                        {faq.a}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Link to="/faq" className="inline-flex min-h-11 items-center self-start text-sm font-bold text-ocean-800 underline dark:text-foam-100">See all questions &amp; answers</Link>
            </section>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
